import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import BaseOption from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/Option';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import get from 'lodash/get';
import find from 'lodash/find';
import Markdown from '@configuratorware/configurator-frontendgui/Framework/Components/Markdown';
import {
  applyOptionInputText,
  resetOptionInputTexts,
  doCalculation,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { OptionProps } from './types';
import { useStyles } from './styles';
import { AttributeIdentifiers } from '../../Custom/enums';

const OptionContent = ({
  classes,
  imageAvailable,
  renderImage,
  renderNotAvailableImage,
  onInputTextChange,
  optionclassification,
  smallerScreen,
  renderControls,
  option,
  renderActions,
}: OptionProps) => {
  const classNames = useStyles();
  const dispatch = useDispatch();
  const { selectedoptions } = optionclassification;
  const [inputText, setInputText] = useState(
    get(find(selectedoptions, ['identifier', option.identifier]), 'inputText', '')
  );
  const [invalidValueError, setInvalidValueError] = useState(false);
  // we don't show any prices https://redhotmagma.atlassian.net/browse/SCS-37
  const hidePrices = true;
  const itemIdentifier = useSelector((state) =>
    get(state, 'configurator.configuration.item.identifier', null)
  );
  const configuration = useSelector((state) => get(state, 'configurator.configuration', null));

  const minAttributeValue = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MIN_VALUE]),
    'values[0].value',
    0
  );
  const maxAttributeValue = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MAX_VALUE]),
    'values[0].value',
    0
  );
  const minWidthValueDouble = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MIN_VALUE_DOUBLE]),
    'values[0].value',
    0
  );
  const maxWidthValueDouble = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MAX_VALUE_DOUBLE]),
    'values[0].value',
    0
  );
  const minWidthValueSingle = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MIN_VALUE_SINGLE]),
    'values[0].value',
    0
  );
  const maxWidthValueSingle = get(
    find(option.attributes, ['identifier', AttributeIdentifiers.MAX_VALUE_SINGLE]),
    'values[0].value',
    0
  );
  let minValue = minAttributeValue;
  let maxValue = maxAttributeValue;
  if (itemIdentifier === 'scs_carport_double' && option.identifier === 'carport_width') {
    minValue = minWidthValueDouble;
    maxValue = maxWidthValueDouble;
  } else if (itemIdentifier === 'scs_carport_single' && option.identifier === 'carport_width') {
    minValue = minWidthValueSingle;
    maxValue = maxWidthValueSingle;
  }

  const step = get(find(option.attributes, ['identifier', AttributeIdentifiers.STEP]), 'values[0].value', 0);
  const hasTextinput = get(option, 'hasTextinput', false);

  const handleTextInputChange = (event) => {
    const value = event.target.value;
    setInvalidValueError(false);
    if (value % step !== 0 || Number(value) < minValue || Number(value) > maxValue) {
      setInvalidValueError(true);
    }
    setInputText(value);
    !invalidValueError && onInputTextChange(option.identifier, value);
  };

  const handleTextInputOnBlur = (event) => {
    handleTextInputChange(event);
    if (!invalidValueError) {
      return dispatch(applyOptionInputText(optionclassification)).then(() => {
        dispatch(doCalculation(configuration));
      });
    } else {
      // reset to previous value if input is invalid when leaving the input field and reset error
      setInputText(get(find(selectedoptions, ['identifier', option.identifier]), 'inputText', ''));
      setInvalidValueError(false);
    }
  };

  const handleAddClick = async (event) => {
    event.stopPropagation();
    const value = Number(inputText) + Number(step);
    await dispatch(resetOptionInputTexts());
    if (value <= maxValue) {
      setInputText(value);
      await onInputTextChange(option.identifier, value.toString());
      return dispatch(applyOptionInputText(optionclassification)).then(() => {
        dispatch(doCalculation(configuration));
      });
    }
  };

  const handleRemoveClick = async (event) => {
    event.stopPropagation();
    const value = Number(inputText) - step;
    await dispatch(resetOptionInputTexts());
    if (value >= minValue) {
      setInputText(value);
      await onInputTextChange(option.identifier, value.toString());
      return dispatch(applyOptionInputText(optionclassification)).then(() => {
        dispatch(doCalculation(configuration));
      });
    }
  };

  const handleTextClick = (event) => {
    event.stopPropagation();
  };

  const renderTextInput = () => {
    return (
      <>
        <ButtonGroup className={classNames.Buttongroup}>
          <Button
            className={classNames.removeButton}
            color="primary"
            variant="contained"
            onClick={handleRemoveClick}
            disabled={inputText == minValue}
          >
            <RemoveIcon />
          </Button>
          <TextField
            key={option.identifier + '_inputText'}
            value={inputText}
            type={'number'}
            onChange={handleTextInputChange}
            onBlur={handleTextInputOnBlur}
            className={classNames.textInput}
            error={invalidValueError}
            variant="standard"
            inputProps={{
              min: minValue,
              max: maxValue,
              step: step,
            }}
            InputProps={{
              className: classNames.textInput,
            }}
            onClick={handleTextClick}
          />
          <Button
            className={classNames.addButton}
            color="primary"
            variant="contained"
            onClick={handleAddClick}
            disabled={inputText == maxValue}
          >
            <AddIcon />
          </Button>
        </ButtonGroup>
        <Typography
          className={invalidValueError ? classNames.inputHintError : classNames.inputHint}
          variant={'caption'}
        >
          {T('option.inputHint', {
            min: minValue,
            max: maxValue,
            optionTitle: option.title,
            steps: step,
          })}
        </Typography>
      </>
    );
  };
  const renderText = () => {
    return (
      <Grid item xs={12}>
        <Typography component={'div'} className={classes.optionList} variant={'body2'}>
          <div className={classNames.title}>{option.title}</div>
          {!hidePrices && <div className={classes.price}>{option.priceFormatted}</div>}
          {smallerScreen && renderControls('horizontal')}
          {option.abstract && <Markdown className={classes.description}>{option.abstract}</Markdown>}
          {renderActions()}
          {hasTextinput && renderTextInput()}
        </Typography>
      </Grid>
    );
  };

  return (
    <div>
      <div className={classes.image}>{imageAvailable ? renderImage() : renderNotAvailableImage()}</div>
      <div className={classes.text}>{renderText()}</div>
      {!smallerScreen && renderControls()}
    </div>
  );
};

const Option = (props: OptionProps) => {
  return <BaseOption {...props}>{OptionContent}</BaseOption>;
};

export default Option;
